import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type { TSupportInfo } from '@timeedit/types';
import api from '../services/api.service';

type TSupportInfoState = IDefaultReducerState & {
  supportInfo: TSupportInfo;
  updatedSupportInfo: TSupportInfo;
};

const createEmptySupportInfo = (): TSupportInfo => ({
  generalDescription: '',
  generalName: '',
  generalEmail: '',
  generalPhone: '',
  generalUrl: '',
  generalUrlTitle: '',
  generalManualUrl: '',
  generalManualUrlTitle: '',
  technicalDescription: '',
  technicalName: '',
  technicalEmail: '',
  technicalPhone: '',
  technicalUrl: '',
  technicalUrlTitle: '',
  technicalManualUrl: '',
  technicalManualUrlTitle: '',
});

export const initialState: TSupportInfoState = {
  loading: false,
  hasErrors: false,
  supportInfo: createEmptySupportInfo(),
  updatedSupportInfo: createEmptySupportInfo(),
};

// Slice
const slice = createSlice({
  name: 'supportInfo',
  initialState,
  reducers: {
    fetchSupportInfoRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchSupportInfoSuccess: (
      state,
      { payload }: PayloadAction<TSupportInfo>
    ) => {
      state.supportInfo = payload;
      state.updatedSupportInfo = payload;
      finishedLoadingSuccess(state);
    },
    fetchSupportInfoFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setsUpdatedSupportInfo: (
      state,
      { payload }: PayloadAction<TSupportInfo>
    ) => {
      state.updatedSupportInfo = payload;
    },
    updateSupportInfoRequest: (state) => {
      isLoadingRequest(state);
    },
    updateSupportInfoSuccess: (
      state,
      { payload }: PayloadAction<TSupportInfo>
    ) => {
      state.supportInfo = payload;
      state.updatedSupportInfo = payload;
      finishedLoadingSuccess(state);
    },
    updateSupportInfoFailure: (state) => {
      finishedLoadingFailure(state);
    },
    discardChange: (state) => {
      state.updatedSupportInfo = state.supportInfo;
    },
  },
});

export default slice.reducer;

// Selectors
export const supportInfoLoading = (state: TRootState) =>
  state.supportInfo.loading;
export const updatedSupportInfoSelector = (state: TRootState) =>
  state.supportInfo.updatedSupportInfo;

// Actions
export const {
  fetchSupportInfoRequest,
  fetchSupportInfoSuccess,
  fetchSupportInfoFailure,
  setsUpdatedSupportInfo,
  updateSupportInfoRequest,
  updateSupportInfoSuccess,
  updateSupportInfoFailure,
  discardChange,
} = slice.actions;

export const fetchSupportInfo = createAppAsyncThunk(
  'supportInfo/fetchSupportInfo',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchSupportInfoRequest());
      const supportInfo = await api.get({
        endpoint: `/support-info`,
      });
      dispatch(fetchSupportInfoSuccess(supportInfo));
    } catch (e) {
      dispatch(fetchSupportInfoFailure());
      return console.error(e);
    }
  }
);

export const updateSupportInfo = createAppAsyncThunk<void, TSupportInfo>(
  'supportInfo/updateSupportInfo',
  async (supportInfo, { dispatch }) => {
    try {
      dispatch(updateSupportInfoRequest());
      const { ...supportInfoBody } = supportInfo;
      const response = await api.patch({
        endpoint: `/support-info`,
        data: { ...supportInfoBody },
      });
      dispatch(updateSupportInfoSuccess(response));
    } catch (e: any) {
      dispatch(updateSupportInfoFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);
